// @ts-nocheck
import * as O from 'fp-ts/lib/Option';
import {
  changeCommissionFilter,
  clearCommissions,
  EmptyCommissionFilter,
  getAdvancedCommissions,
  getEarnedCommissions,
  changeSort,
} from 'Agent/Commissions/ducks/commission';
import {
  Tab,
  Tabs,
  useMediaQuery,
  Button,
  createStyles,
  makeStyles,
  Theme,
  Tooltip,
  withStyles,
  MenuItem,
  Menu,
} from '@material-ui/core';
import {Commission} from 'Agent/Commissions/models/Commission';
import {theme} from 'Core/components/WithTheme';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import Column from 'Shared/components/PagedView/Column';
import {
  withDateFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {getSourceOptions} from 'Shared/constants/Source';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {Info} from '@material-ui/icons';
import {A360CommissionsDashboard} from './A360Commissions/DataDashboard/CommissionsDashboard';
import './Commissions.sass';
import {CommissionReportingView} from './A360Commissions/Reporting/CommissionsReportingView';

type CommissionsProps = {
  selectedAgency: AgencyFilters;
} & RouteComponentProps;

enum CommissionTab {
  Earned = 0,
  Advanced = 1,
  DataDashboard = 2,
  Reporting = 3,
}

const useStyles: any = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(3),
    },
  })
);
const DataDashboard = withStyles({
  tooltip: {
    color: 'black',
    backgroundColor: 'white',
    fontWeight: 'bold',
    fontSize: '12px',
    border: '1px solid',
    wordSpacing: '4px 20px',
    whiteSpace: 'nowrap',
    outline: 'none',
    position: 'relative',
    maxwidth: '100%',
    minWidth: '75px',
    maxWidth: '1025px',
    lineHeight: '35px',
  },
})(Tooltip);
const InfoIcon = makeStyles((theme: Theme) =>
  createStyles({
    tooltip: {
      color: '#1E90FF',
      backgroundColor: 'transparent',
      position: 'relative',
    },
  })
);

export const getCommissionDownloadFileName = (commission: Commission, fileName: string) =>
  `${fileName}${toFormattedDate(commission.statementDate)}.pdf`;

export const Commissions = (props: CommissionsProps) => {
  const dispatch = useDispatch();
  const {selectedAgency} = props;

  const {agentNumbers, isNewYork, encryptedIdentifier} = selectedAgency;
  useBreadcrumbs('Commissions', props.match.url);
  const [currentTab, setCurrentTab] = useState(CommissionTab.Earned);
  const {commissions, sort, commissionsFilter: filter} = useSelector((state: ReducerState) => state.commissions);
  const sortAsUndefined = sort ? O.toUndefined(sort) : undefined;

  useEffect(() => {
    return () => {
      dispatch(clearCommissions());
      dispatch(changeCommissionFilter(EmptyCommissionFilter));
    };
  }, []);

  const getCommissions = currentTab === CommissionTab.Earned ? getEarnedCommissions : getAdvancedCommissions;

  const updateTab = (newValue: number) => {
    setCurrentTab(newValue);
  };
  useEffect(() => {
    if (![CommissionTab.DataDashboard, CommissionTab.Reporting].includes(currentTab))
      dispatch(getCommissions(agentNumbers, isNewYork, encryptedIdentifier, undefined, filter, sortAsUndefined));
  }, [currentTab, filter, sortAsUndefined, agentNumbers, isNewYork, encryptedIdentifier]);

  const changeFilter = withValueChangedHook(filter, changeCommissionFilter, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: filter,
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: currentTab === 0 ? 'FilteredEarnedCommissions' : 'FilteredAdvancedCommissions',
    relativeFileLocation: `/api/commissions/v1/${
      currentTab === 0 ? 'earnedStatements' : 'advancedStatements'
    }${addFilterParameters({
      ...filter,
      agentNumbers: selectedAgency.agentNumbers,
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
      exportToCsv: true,
    })}`,
    children: 'Export To CSV',
  };

  const tooltipClass = InfoIcon();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (newValue: number) => {
    updateTab(newValue);
    handleClose();
  };

  const CommissionTabHeader = isMobile ? (
    <div className="MuiButtonBase-root MuiButton-root MuiButton-text commissions__menu-button MuiButton-textPrimary MuiButton-fullWidth">
      <Button
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <div className="commissions__menu-button-text">Commission Reports&nbsp;&nbsp;&#x25BC;</div>
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: '90%',
            maxWidth: '90%',
            left: 0,
            right: 0,
            transform: 'translateY(34%)',
          },
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem
          aria-label="view earned commissions"
          color="primary"
          id={`view-earned-commissions`}
          onClick={() => handleMenuClick(0)}
          style={{marginRight: '0'}}
        >
          Earned Commissions
        </MenuItem>
        <MenuItem
          aria-label="view advanced commissions"
          color="primary"
          id={`view-advanced-commissions`}
          style={{marginRight: '0'}}
          onClick={() => handleMenuClick(1)}
        >
          Advanced Commissions
        </MenuItem>

        <MenuItem
          aria-label="view data dashboard"
          color="primary"
          id={`view-data-dashboard`}
          onClick={() => handleMenuClick(2)}
        >
          Data Dashboard
        </MenuItem>
        <MenuItem
          aria-label="view access360 commissions dashboard"
          color="primary"
          id={`view-access360-reports`}
          onClick={() => handleMenuClick(3)}
        >
          Reporting
        </MenuItem>
      </Menu>
    </div>
  ) : (
    <section className="commissions__tabs" aria-labelledby="commissions page tabs">
      <Tabs
        value={currentTab}
        onChange={(event, newValue) => {
          updateTab(newValue);
        }}
        className="commissions__tabs-header"
      >
        <Tab label="Earned Commissions" className="commissions__tab" />
        <Tab label="Advanced Commissions" className="commissions__tab" />
        <DataDashboard
          placement="top"
          title={
            <>
              <Info className={tooltipClass.tooltip} style={{transform: 'translate(1px,6px)'}} /> Data Dashboard is
              currently only available for accounts in our new admin system.{' '}
            </>
          }
        >
          <Tab label="Data Dashboard" className="commissions__tab" />
        </DataDashboard>
        <Tab label="Reporting" className="commissions__tab" />
      </Tabs>
    </section>
  );

  return (
    <>
      {CommissionTabHeader}

      {currentTab === CommissionTab.DataDashboard && <A360CommissionsDashboard isNewYork={isNewYork} />}

      {currentTab === CommissionTab.Reporting && <CommissionReportingView isNewYork={isNewYork} />}

      {(currentTab === CommissionTab.Earned || currentTab === CommissionTab.Advanced) && (
        <PagedView
          pagedViewKey="commissions"
          eventAction="agentEarnedCommisionsSettingFilter"
          loadMore={page =>
            dispatch(
              getCommissions(
                selectedAgency.agentNumbers,
                selectedAgency.isNewYork,
                selectedAgency.encryptedIdentifier,
                page,
                filter,
                sortAsUndefined
              )
            )
          }
          data={commissions}
          sort={sortAsUndefined}
          onChangeSort={sort => dispatch(changeSort(sort))}
          actions={
            isMobile ? (
              <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
            ) : (
              <FileDownloadButton
                variant="contained"
                color="primary"
                displayTooltip
                {...exportToCsvProps}
                exportToCsvEvent="agentEarnedCommisions_exportToCsv"
              />
            )
          }
        >
          {columnProps => (
            <>
              <Column
                {...columnProps}
                title="Agent Name"
                filterProperties={withStringFilterProperties(filterProperties, 'agentName')}
                sortColumnKey="agentName"
                default
              >
                {commission => commission.agentName}
              </Column>
              <Column
                {...columnProps}
                title="Agent Number"
                filterProperties={withStringFilterProperties(filterProperties, 'agentNumber')}
                sortColumnKey="agentNumber"
                default
              >
                {commission => commission.agentNumber}
              </Column>
              <Column
                {...columnProps}
                title="Statement Date"
                filterProperties={withDateFilterProperties(filterProperties, 'statement')}
                sortColumnKey="statementDate"
                default
              >
                {commission => toFormattedDate(commission.statementDate)}
              </Column>
              <Column
                {...columnProps}
                title="Source"
                filterProperties={withSelectFilterProperties(
                  filterProperties,
                  'sourceId',
                  getSourceOptions(selectedAgency.isNewYork)
                )}
                sortColumnKey="source"
                default
              >
                {commission => commission.source!.name}
              </Column>
              <Column {...columnProps} isActions title="Document" default>
                {commission =>
                  commission.links[0] != null ? (
                    <div className="commissions__document-link">
                      {/* The API already added .pdf here, avoid double extensions */}
                      <FileDownloadButton
                        downloadedFileName={getCommissionDownloadFileName(commission, 'Commission-Statement-')}
                        displayTooltip={false}
                        relativeFileLocation={
                          '/api/commissions' +
                          commission.links[0].href +
                          addFilterParameters({encryptedIdentifier: selectedAgency.encryptedIdentifier})
                        }
                        exportToCsvEvent="agentEarnedCommisions_download"
                        aria-label="Download Document"
                      >
                        Download
                      </FileDownloadButton>
                    </div>
                  ) : (
                    <div>N/A</div>
                  )
                }
              </Column>
              <Column {...columnProps} isActions title="Disbursement" default>
                {commission =>
                  commission.links[1] != null ? (
                    <div className="commissions__document-link">
                      <FileDownloadButton
                        downloadedFileName={getCommissionDownloadFileName(commission, 'Commission-Deposit-Statement-')}
                        displayTooltip={false}
                        relativeFileLocation={
                          '/api/commissions' +
                          commission.links[1].href +
                          addFilterParameters({encryptedIdentifier: selectedAgency.encryptedIdentifier})
                        }
                        exportToCsvEvent="agentEarnedCommisions_download"
                        aria-label="Download Disbursement"
                      >
                        Download
                      </FileDownloadButton>
                    </div>
                  ) : (
                    <div>N/A</div>
                  )
                }
              </Column>
            </>
          )}
        </PagedView>
      )}
    </>
  );
};

export default Commissions;
